import React, { useEffect, useRef, useState } from 'react';
import { onboardingAPI } from '../../services/api';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { FilePdf, UploadSimple, PencilSimpleLine, Trash, SquaresFour } from '@phosphor-icons/react';
import { toast } from 'sonner';
import PdfFieldEditor from './PdfFieldEditor';
import RequiredDocumentsSection from './RequiredDocumentsSection';

const PacketFormsSection = ({ onChanged }) => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [editing, setEditing] = useState(null);
  const fileRef = useRef(null);

  const load = async () => {
    try { const res = await onboardingAPI.listTemplates(); setTemplates(res.data); }
    catch { toast.error('Failed to load packet forms'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.pdf')) { toast.error('Please upload a PDF file'); return; }
    setUploading(true);
    try {
      const res = await onboardingAPI.uploadTemplate(file);
      toast.success(`${file.name} uploaded — now place the fill boxes`);
      await load();
      setEditing(res.data);
      onChanged?.();
    } catch (err) { toast.error(err.response?.data?.detail || 'Upload failed'); }
    finally { setUploading(false); }
  };

  const togglePacket = async (t, v) => {
    try { await onboardingAPI.updateTemplate(t.id, { in_packet: v }); setTemplates(prev => prev.map(x => (x.id === t.id ? { ...x, in_packet: v } : x))); onChanged?.(); }
    catch { toast.error('Failed to update'); }
  };

  const handleDelete = async (t) => {
    if (!window.confirm(`Delete "${t.name}"? Applicants who already signed it keep their copy.`)) return;
    try { await onboardingAPI.deleteTemplate(t.id); setTemplates(prev => prev.filter(x => x.id !== t.id)); toast.success('Form deleted'); onChanged?.(); }
    catch { toast.error('Failed to delete'); }
  };

  return (
    <div className="space-y-4" data-testid="packet-forms-section">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <p className="text-sm text-muted-foreground">Every new applicant receives all forms marked <span className="font-medium text-foreground">In packet</span>. Upload your existing PDF forms and place fill boxes + signature boxes on them.</p>
        <input ref={fileRef} type="file" accept="application/pdf,.pdf" className="hidden" onChange={handleUpload} data-testid="upload-pdf-input" />
        <Button className="rounded-xl gap-2 shrink-0" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="upload-pdf-btn">
          <UploadSimple weight="bold" className="w-4 h-4" /> {uploading ? 'Uploading…' : 'Upload PDF Form'}
        </Button>
      </div>

      {loading ? <div className="py-10 text-center text-sm text-muted-foreground">Loading…</div> : templates.length === 0 ? (
        <Card className="rounded-2xl border-dashed"><CardContent className="py-12 flex flex-col items-center text-center">
          <FilePdf weight="duotone" className="w-14 h-14 text-muted-foreground/50 mb-3" />
          <h3 className="font-heading text-lg font-medium">No onboarding forms yet</h3>
          <p className="text-sm text-muted-foreground mt-1 max-w-sm">Upload your W-4, I-9, direct deposit, handbook acknowledgement, or any other PDF and mark where applicants should fill in and sign.</p>
        </CardContent></Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {templates.map(t => {
            const sigs = (t.fields || []).filter(f => f.type === 'signature' || f.type === 'initials').length;
            return (
              <Card key={t.id} className={`rounded-2xl transition-all ${t.in_packet ? '' : 'opacity-70'}`} data-testid={`packet-template-${t.id}`}>
                <CardContent className="p-5 space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="w-11 h-11 rounded-xl bg-rose-50 text-rose-600 flex items-center justify-center shrink-0"><FilePdf weight="duotone" className="w-6 h-6" /></div>
                    <div className="min-w-0 flex-1">
                      <p className="font-medium truncate">{t.name}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">{t.page_count} page{t.page_count !== 1 ? 's' : ''} · {(t.fields || []).length} field{(t.fields || []).length !== 1 ? 's' : ''} · {sigs} signature{sigs !== 1 ? 's' : ''}</p>
                    </div>
                  </div>
                  {(t.fields || []).length === 0 && <Badge variant="outline" className="rounded-full text-[11px] text-amber-700 border-amber-300 bg-amber-50 gap-1"><SquaresFour className="w-3 h-3" /> No fill boxes placed yet</Badge>}
                  <div className="flex items-center justify-between gap-2">
                    <label className="flex items-center gap-2 text-xs"><Switch checked={!!t.in_packet} onCheckedChange={(v) => togglePacket(t, v)} data-testid={`packet-toggle-${t.id}`} /> In packet</label>
                    <div className="flex items-center gap-1">
                      <Button variant="outline" size="sm" className="rounded-xl gap-1" onClick={() => setEditing(t)} data-testid={`edit-template-${t.id}`}><PencilSimpleLine weight="bold" className="w-3.5 h-3.5" /> Fields</Button>
                      <Button variant="ghost" size="sm" className="rounded-xl text-destructive h-8 w-8 p-0" onClick={() => handleDelete(t)} data-testid={`delete-template-${t.id}`}><Trash className="w-4 h-4" /></Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <PdfFieldEditor template={editing} open={!!editing} onClose={() => setEditing(null)} onSaved={(t) => { setTemplates(prev => prev.map(x => (x.id === t.id ? t : x))); onChanged?.(); }} />

      <div className="pt-6 border-t border-border">
        <RequiredDocumentsSection onChanged={onChanged} />
      </div>
    </div>
  );
};

export default PacketFormsSection;
